<template>
  <div class="page-container">
    <div class="page-title-container d-flex justify-content-between align-items-center">
      <h1 class="page-title mb-0">Bitácora de {{ driverName }}</h1>
      <b-button
        variant="none"
        class="button button-light"
        :to="{ name: 'config-driver-list' }"
      >
        Volver
      </b-button>
    </div>

    <div class="table-container">
      <!-- Filtros -->
      <div class="table-header">
        <div class="w-full d-flex flex-wrap align-items-center">
          <div class="d-flex align-items-center mr-2 mb-2 mb-md-0">
            <label class="filter-label mb-0 mr-2">Desde</label>
            <input type="date" v-model="from" class="toolbar-select" />
          </div>
          <div class="d-flex align-items-center mr-2 mb-2 mb-md-0">
            <label class="filter-label mb-0 mr-2">Hasta</label>
            <input type="date" v-model="to" class="toolbar-select" />
          </div>

          <b-button
            variant="none"
            class="button button-primary mr-2 mb-2 mb-md-0"
            @click="loadData"
          >
            Filtrar
          </b-button>

          <b-button
            variant="none"
            class="button button-light mr-2 mb-2 mb-md-0"
            @click="downloadReport"
          >
            <i class="ri-download-2-line mr-1"></i> Exportar Excel
          </b-button>

          <b-button
            v-if="dayShiftId"
            variant="none"
            class="button button-primary mb-2 mb-md-0"
            @click="goToRoute"
          >
            <i class="ri-route-line mr-1"></i> Ver recorrido del día
          </b-button>
        </div>
      </div>

      <!-- Tabla de actividad -->
      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-3">Hora</th>
              <th>Acción</th>
              <th>Viaje</th>
              <th>Detalle</th>
              <th class="pr-md-4">Evidencia</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.id">
              <td class="text-center pl-3">{{ formatHour(o.created_at) }}</td>
              <td class="text-center">
                <span :class="`badge ${actionMeta(o.action).color}`">
                  {{ actionMeta(o.action).label }}
                </span>
              </td>
              <td class="text-center">
                <span v-if="o.ride_code">{{ o.ride_code }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">{{ o.detail || '—' }}</td>
              <td class="text-center pr-md-4">
                <button
                  v-if="o.action === 'SHIFT_START' && shiftIdFromDetail(o.detail)"
                  type="button"
                  class="btn-evidence"
                  @click="openShiftPhoto(shiftIdFromDetail(o.detail))"
                >
                  📷 Ver foto
                </button>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <div class="table-no-results" v-if="!list.length">
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
      </div>
    </div>

    <!-- Resumen del día -->
    <div class="table-container mt-3" v-if="summary">
      <div class="summary-card">
        <h2 class="summary-title">Resumen del día ({{ formatDate(summary.date) }})</h2>
        <div class="d-flex flex-wrap">
          <div class="summary-item">
            <span class="summary-label">Turno</span>
            <span class="summary-value">{{ shiftRangeText }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Viajes completados</span>
            <span class="summary-value">{{ summary.rides_completed }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Non Show</span>
            <span class="summary-value">{{ summary.non_shows }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Cancelados</span>
            <span class="summary-value">{{ summary.cancelled }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Km recorridos (odómetro)</span>
            <span class="summary-value">{{ odometerKmText }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Alert } from 'wize-admin'
import { ActivityLogService, DriverService } from '@/core/services'

// Colores alineados con los estados del tracking (DEF-ADM-011 v2.0).
const ACTIONS = {
  SHIFT_START:      { label: 'Inicio turno',   color: 'badge-success' },
  SHIFT_END:        { label: 'Fin turno',      color: 'badge-secondary' },
  SHIFT_FORCED_END: { label: 'Relevo forzado', color: 'badge-danger' },
  RIDE_ASSIGNED:    { label: 'Viaje asignado', color: 'badge-info' },
  RIDE_EN_CAMINO:   { label: 'En camino',      color: 'badge-primary' },
  RIDE_UBICADO:     { label: 'Ubicado',        color: 'badge-teal' },
  RIDE_EN_PROCESO:  { label: 'En proceso',     color: 'badge-amber' },
  RIDE_FINALIZADO:  { label: 'Finalizado',     color: 'badge-success' },
  RIDE_NON_SHOW:    { label: 'Non Show',       color: 'badge-dark' },
  RIDE_CANCELADO:   { label: 'Cancelado',      color: 'badge-danger' }
}

function today() {
  return new Date().toISOString().slice(0, 10)
}

export default {
  name: 'DriverActivityPage',
  data() {
    return {
      driverId: this.$route.params.id,
      driverName: '',
      list: [],
      summary: null,
      from: today(),
      to: today()
    }
  },
  computed: {
    // Timezone IANA de la ciudad activa; los timestamps se guardan en UTC y se
    // convierten para mostrar. Fallback a America/Lima si no hay ciudad activa.
    cityTz() {
      const city = this.$store.getters['city/getCurrentCity']
      return city && city.timezone ? city.timezone : 'America/Lima'
    },
    // Shift del día: se extrae del primer SHIFT_START de la bitácora cargada
    // (su detail es "Turno #<id>"). null si el rango no tiene inicio de turno.
    dayShiftId() {
      const start = this.list.find(o => o.action === 'SHIFT_START' && this.shiftIdFromDetail(o.detail))
      return start ? this.shiftIdFromDetail(start.detail) : null
    },
    // Km del día por odómetro (DEF-ADM-014). "Sin registro" si el backend no
    // tiene odómetro para ningún turno del día.
    odometerKmText() {
      if (!this.summary || this.summary.odometer_km_total == null) return 'Sin registro'
      return `${Number(this.summary.odometer_km_total).toLocaleString('en-US')} km`
    },
    // "06:02 — 14:30 (8h 28m)" | "06:02 — en curso" | "Sin turno registrado"
    shiftRangeText() {
      if (!this.summary || !this.summary.shift_start) return 'Sin turno registrado'
      const start = this.formatHour(this.summary.shift_start)
      if (!this.summary.shift_end) return `${start} — en curso`
      const end = this.formatHour(this.summary.shift_end)
      return `${start} — ${end} (${this.formatDuration(this.summary.duration_minutes)})`
    }
  },
  methods: {
    actionMeta(action) {
      return ACTIONS[action] || { label: action, color: 'badge-secondary' }
    },
    // El detail de SHIFT_START es "Turno #<id>"; extrae el id para pedir la foto.
    shiftIdFromDetail(detail) {
      if (!detail) return null
      const m = String(detail).match(/#(\d+)/)
      return m ? m[1] : null
    },
    // Navega a la vista de recorrido del turno del día (DEF-ADM-013).
    goToRoute() {
      if (!this.dayShiftId) return
      this.$router.push({ name: 'shift-route', params: { id: this.dayShiftId } })
    },
    async openShiftPhoto(shiftId) {
      try {
        await this.$store.dispatch('app/loading', true)
        const response = await ActivityLogService.getShiftPhoto(shiftId)
        const url = response.payload ? response.payload.url : null
        if (!url) {
          Alert.error('No se encontró la foto del turno')
          return
        }
        window.open(url, '_blank')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al obtener la foto del turno')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    formatHour(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (isNaN(d.getTime())) return '—'
      return d.toLocaleTimeString('es-PE', {
        timeZone: this.cityTz,
        hour: '2-digit', minute: '2-digit', hour12: false
      })
    },
    formatDate(value) {
      if (!value) return ''
      const parts = String(value).slice(0, 10).split('-')
      if (parts.length !== 3) return value
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },
    formatDuration(minutes) {
      if (!minutes || minutes <= 0) return '0m'
      const h = Math.floor(minutes / 60)
      const m = minutes % 60
      return h ? `${h}h ${m}m` : `${m}m`
    },
    async loadDriver() {
      try {
        const response = await DriverService.get(this.driverId)
        this.driverName = response.payload ? response.payload.full_name : ''
      } catch (e) {
        console.error(e)
      }
    },
    async loadData() {
      if (!this.from || !this.to) {
        Alert.error('Selecciona el rango de fechas')
        return
      }
      try {
        await this.$store.dispatch('app/loading', true)
        const activity = await ActivityLogService.getDriverActivity(this.driverId, this.from, this.to, this.cityTz)
        this.list = activity.payload || []

        // El resumen es por día: se usa la fecha "desde".
        const summary = await ActivityLogService.getDriverSummary(this.driverId, this.from, this.cityTz)
        this.summary = summary.payload || null
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al obtener la bitácora')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReport() {
      try {
        await this.$store.dispatch('app/loading', true)
        const response = await ActivityLogService.exportDriverActivity(this.driverId, this.from, this.to, this.cityTz)
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `Bitacora_${this.driverName || this.driverId}_${this.from}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al exportar la bitácora')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    }
  },
  async mounted() {
    await this.loadDriver()
    await this.loadData()
  }
}
</script>

<style lang="stylus" scoped>
.filter-label
  font-size 13px
  color #6c757d
  white-space nowrap

.btn-evidence
  display inline-flex
  align-items center
  gap 4px
  padding 4px 12px
  font-size 13px
  font-weight 600
  color #fff
  background-color #28a745
  border none
  border-radius 4px
  cursor pointer
  transition background-color .15s ease
.btn-evidence:hover
  background-color #218838

// Badges sin equivalente directo en Bootstrap.
// UBICADO usa color-violet (#4D3C81), el mismo que la lista de Viajes y el
// portal de afiliados: es el mismo estado y tenía un color por pantalla.
.badge-teal
  background-color #4D3C81
  color #fff
.badge-amber
  background-color #f59e0b
  color #fff

.summary-card
  padding 18px 20px

.summary-title
  font-size 15px
  font-weight 600
  margin-bottom 14px

.summary-item
  display flex
  flex-direction column
  min-width 190px
  margin-right 28px
  margin-bottom 8px

.summary-label
  font-size 11px
  text-transform uppercase
  letter-spacing .06em
  color #8592A6

.summary-value
  font-size 17px
  font-weight 600
  margin-top 2px
</style>
